import { Component, computed, input, output } from '@angular/core';
import {
  MatCard,
  MatCardHeader,
  MatCardTitle,
  MatCardSubtitle,
  MatCardContent
} from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { DatePipe } from '@angular/common';
import { RelativeTimePipe, TruncatePipe } from '@htx/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { MatIconButton } from '@angular/material/button';
import { CardConfig, DateInput } from '@htx/shared';

interface UiDateRange {
  start: Date;
  end: Date;
}

@Component({
  selector: 'app-policy-contract-entity-card',
  imports: [
    MatCard,
    MatCardHeader,
    MatIcon,
    MatIconButton,
    MatCardTitle,
    MatCardSubtitle,
    MatCardContent,
    MatDivider,
    DatePipe,
    TranslocoPipe,
    RelativeTimePipe,
    TruncatePipe
  ],
  templateUrl: './policy-contract-entity-card.component.html',
  styleUrl: './policy-contract-entity-card.component.scss'
})
export class PolicyContractEntityCardComponent<T> {
  readonly item = input.required<T>();
  readonly showDeleteIcon = input.required<boolean>();
  readonly cardConfig = input.required<CardConfig<T>>();
  readonly iconClick = output<void>();
  readonly titleClick = output<void>();

  creationDate = input<number>();

  finalCfg = computed(() => {
    return this.cardConfig().contentConfig.map((cfg) => {
      const resolved = cfg.factory?.(this.item()) ?? cfg;
      if (!('type' in resolved) || !resolved.type) {
        (resolved as { type: string }).type = 'STRING';
      }
      return resolved;
    });
  });

  getValue(cfg: string | ((entity: T) => string)): string {
    return typeof cfg === 'string' ? cfg : cfg(this.item());
  }

  getImage(): string {
    const cardCfg = this.cardConfig();
    if (cardCfg.image) {
      return typeof cardCfg.image === 'function' ? cardCfg.image(this.item()) : cardCfg.image;
    }
    const content = cardCfg.contentConfig.find((content) => !!content.factory);
    if (content) {
      const config = content.factory!(this.item());
      if (config && typeof config.image === 'function') {
        return config.image(this.item());
      }
      return (config && (config as { image?: string }).image) || '';
    }
    return '';
  }

  // ---- Type Guards & Konverter ----
  private isDate(value: unknown): value is Date {
    return value instanceof Date && !isNaN(value.getTime());
  }

  private isDateInput(value: unknown): value is DateInput {
    return this.isDate(value) || typeof value === 'string';
  }

  private isDateRange(value: unknown): value is UiDateRange {
    if (!value || typeof value !== 'object') return false;
    const v = value as { start?: unknown; end?: unknown };
    return this.isDate(v.start) && this.isDate(v.end);
  }

  private isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every((x) => typeof x === 'string');
  }

  asDateRange(obj: unknown): UiDateRange | null {
    return this.isDateRange(obj) ? obj : null;
  }

  asDate(obj: unknown): DateInput | null {
    return this.isDateInput(obj) ? obj : null;
  }

  asList(obj: unknown): string[] {
    return this.isStringArray(obj) ? obj : [];
  }

  asString(obj: unknown): string {
    return typeof obj === 'string' ? obj : '';
  }
}
